import axios from "axios";
import {
  clearStoredToken,
  getStoredRefreshToken,
  getStoredToken,
  persistRefreshToken,
  persistToken,
} from "../auth/session";

const defaultApiUrl =
  process.env.REACT_APP_API_URL ||
  `${window.location.protocol}//${window.location.hostname}:3033`;

const api = axios.create({
    baseURL: defaultApiUrl,
});

let refreshPromise = null;
let refreshPromiseToken = "";
let sessionLossNotified = false;
const sessionLossListeners = new Set();

export const subscribeToSessionLoss = (listener) => {
  sessionLossListeners.add(listener);

  return () => {
    sessionLossListeners.delete(listener);
  };
};

export const markSessionActive = () => {
  sessionLossNotified = false;
};

const invalidateSession = () => {
  clearStoredToken();
  delete api.defaults.headers.common.Authorization;

  if (sessionLossNotified) {
    return;
  }

  sessionLossNotified = true;
  sessionLossListeners.forEach((listener) => listener());
};

const requestTokenRefresh = async (refreshToken) => {
  try {
    const response = await axios.post(`${defaultApiUrl}/auth/refresh`, {
      refreshToken,
    });

    if (getStoredRefreshToken() !== refreshToken) {
      return false;
    }

    const nextAuthToken = response.data.authToken || response.data.token;
    const nextRefreshToken = response.data.refreshToken || refreshToken;

    if (!nextAuthToken) {
      invalidateSession();
      return false;
    }

    persistToken(nextAuthToken);
    persistRefreshToken(nextRefreshToken);
    api.defaults.headers.common.Authorization = `Bearer ${nextAuthToken}`;
    return true;
  } catch {
    if (getStoredRefreshToken() === refreshToken) {
      invalidateSession();
    }

    return false;
  }
};

export const refreshCurrentSession = async () => {
  const refreshToken = getStoredRefreshToken();

  if (!refreshToken) {
    invalidateSession();
    return false;
  }

  if (!refreshPromise || refreshPromiseToken !== refreshToken) {
    refreshPromiseToken = refreshToken;
    // Share persistence and the result too: rotating the tokens once must not
    // look like a replaced session to other callers awaiting the same refresh.
    const request = requestTokenRefresh(refreshToken);
    const trackedRequest = request.finally(() => {
      if (refreshPromise === trackedRequest) {
        refreshPromise = null;
        refreshPromiseToken = "";
      }
    });

    refreshPromise = trackedRequest;
  }

  return refreshPromise;
};

api.interceptors.request.use((config) => {
  const token = getStoredToken();

  config.headers = config.headers || {};

  if (token) {
    config._sessionAuthToken = token;

    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const currentAuthToken = getStoredToken();

    if (
      originalRequest?._sessionAuthToken &&
      currentAuthToken &&
      originalRequest._sessionAuthToken !== currentAuthToken
    ) {
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${currentAuthToken}`;
      originalRequest._sessionAuthToken = currentAuthToken;
      return api(originalRequest);
    }

    if (originalRequest?._retry) {
      invalidateSession();
      return Promise.reject(error);
    }

    if (!originalRequest) {
      invalidateSession();
      return Promise.reject(error);
    }

    if (!originalRequest._retry) {
      originalRequest._retry = true;

      if (await refreshCurrentSession()) {
        const nextAuthToken = getStoredToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${nextAuthToken}`;
        originalRequest._sessionAuthToken = nextAuthToken;

        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
